import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { GraduationCap, Lock, Loader2, CheckCircle2, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { LegalFooter } from '../components/LegalFooter';
import { API_URL } from '../utils/api';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const passwordValid = password.length >= 8;
  const passwordsMatch = password === confirm;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!passwordValid || !passwordsMatch) return;
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to reset password. The link may be expired or already used.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex flex-col">
        <div className="flex-1 flex items-center justify-center p-8 bg-white">
          <div className="w-full max-w-md text-center" data-testid="reset-no-token">
            <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="h-7 w-7 text-red-500" />
            </div>
            <h1 className="font-outfit text-2xl font-semibold text-[#1C1C1C] mb-2">Invalid Reset Link</h1>
            <p className="text-[#595959] mb-8">This password reset link is missing or invalid. Please request a new one.</p>
            <Link to="/forgot-password">
              <Button className="bg-[#2C4C3B] hover:bg-[#1C3C2B] text-white rounded-xl px-8 py-5" data-testid="request-new-link-btn">
                Request New Link
              </Button>
            </Link>
          </div>
        </div>
        <LegalFooter variant="compact" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-12">
            <GraduationCap className="h-8 w-8 text-[#2C4C3B]" />
            <span className="font-outfit text-2xl font-semibold text-[#1C1C1C]">FrameEd</span>
          </Link>

          {success ? (
            <div data-testid="reset-success">
              <div className="w-14 h-14 rounded-2xl bg-green-50 flex items-center justify-center mb-6">
                <CheckCircle2 className="h-7 w-7 text-green-600" />
              </div>
              <h1 className="font-outfit text-2xl font-semibold text-[#1C1C1C] mb-2">Password Reset</h1>
              <p className="text-[#595959] mb-8">Your password has been successfully changed. You can now sign in with your new password.</p>
              <Button
                onClick={() => navigate('/login')}
                className="w-full bg-[#2C4C3B] hover:bg-[#1C3C2B] text-white rounded-xl py-5"
                data-testid="go-to-login-btn"
              >
                Sign In
              </Button>
            </div>
          ) : (
            <div data-testid="reset-form">
              <h1 className="font-outfit text-2xl font-semibold text-[#1C1C1C] mb-2">Set a new password</h1>
              <p className="text-[#595959] mb-8">Choose a strong password with at least 8 characters.</p>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 mb-6 text-sm flex items-start gap-2" data-testid="reset-error">
                  <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label htmlFor="password" className="text-sm font-medium text-[#1C1C1C]">New password</Label>
                  <div className="relative mt-1.5">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#595959]" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="At least 8 characters"
                      className="pl-10 pr-10 rounded-xl border-[#E5E5E5] py-5 focus:ring-[#2C4C3B]/30 focus:border-[#2C4C3B]"
                      required
                      minLength={8}
                      data-testid="reset-password-input"
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#595959] hover:text-[#1C1C1C]">
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {password && !passwordValid && (
                    <p className="text-xs text-amber-600 mt-1">Password must be at least 8 characters.</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="confirm" className="text-sm font-medium text-[#1C1C1C]">Confirm new password</Label>
                  <div className="relative mt-1.5">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#595959]" />
                    <Input
                      id="confirm"
                      type={showPassword ? 'text' : 'password'}
                      value={confirm}
                      onChange={e => setConfirm(e.target.value)}
                      placeholder="Re-enter your password"
                      className="pl-10 rounded-xl border-[#E5E5E5] py-5 focus:ring-[#2C4C3B]/30 focus:border-[#2C4C3B]"
                      required
                      data-testid="reset-confirm-input"
                    />
                  </div>
                  {confirm && !passwordsMatch && (
                    <p className="text-xs text-red-500 mt-1">Passwords do not match.</p>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={loading || !passwordValid || !passwordsMatch}
                  className="w-full bg-[#2C4C3B] hover:bg-[#1C3C2B] text-white rounded-xl py-5"
                  data-testid="reset-submit-btn"
                >
                  {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Resetting...</> : 'Reset Password'}
                </Button>
              </form>
            </div>
          )}
        </div>
      </div>
      <LegalFooter variant="compact" />
    </div>
  );
}
